import { useState } from "react";
import { Button, Confirm, ErrorNotice, Field, Input, Panel, StatusBadge, useToast } from "../../components/ui";
import { developerApi } from "../../lib/api";
import { invalidate, useAction } from "../../lib/query";
import { navigate } from "../../lib/router";
import { useClient } from "../../lib/session";
import type { Game } from "../../lib/types";
import type { GameTabProps } from "./Game";

// Same rule as the engine: 2-63 lowercase letters, digits or dashes, not starting with a dash.
const SLUG = "[a-z0-9][a-z0-9\\-]{1,62}";

export function GameSettings({ game }: GameTabProps) {
  return (
    <div className="stack">
      <DetailsPanel game={game} />
      <StatusPanel game={game} />
      <DeletePanel game={game} />
    </div>
  );
}

function DetailsPanel({ game }: { game: Game }) {
  const api = developerApi(useClient());
  const toast = useToast();
  const [name, setName] = useState(game.name);
  const [slug, setSlug] = useState(game.slug);
  const changes = {
    ...(name.trim() !== game.name ? { name: name.trim() } : {}),
    ...(slug !== game.slug ? { slug } : {}),
  };
  const dirty = Object.keys(changes).length > 0;
  const save = useAction(() => api.updateGame(game.id, changes));

  return (
    <Panel title="Name and slug" sub="Renaming keeps the slug. Change the slug separately if you want it to match.">
      <form
        className="row end"
        onSubmit={async (e) => {
          e.preventDefault();
          const g = await save.run();
          if (g) {
            setName(g.name);
            setSlug(g.slug);
            invalidate("dev:game");
            toast("Game updated");
          }
        }}
      >
        <div className="grow" style={{ maxWidth: 320 }}>
          <Field label="Game name">
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
          </Field>
        </div>
        <div className="grow" style={{ maxWidth: 340 }}>
          <Field label="Slug">
            <Input className="input mono" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} required pattern={SLUG} maxLength={63} />
          </Field>
        </div>
        <Button type="submit" variant="primary" busy={save.pending} disabled={!dirty || !name.trim()}>Save changes</Button>
      </form>
      <small className="muted" style={{ display: "block", marginTop: 8, fontSize: "0.8rem" }}>The slug can use lowercase letters, digits and dashes.</small>
      <div style={{ marginTop: 12 }}><ErrorNotice error={save.error} /></div>
    </Panel>
  );
}

function StatusPanel({ game }: { game: Game }) {
  const [open, setOpen] = useState(false);
  const active = game.status === "active";
  return (
    <Panel
      title="Status"
      sub={active
        ? "Suspending pauses the game: every API key is refused, in sandbox and production, until you reactivate it."
        : "This game is suspended. Its API keys are refused until it is reactivated."}
      actions={<StatusBadge status={game.status} />}
    >
      <Button variant={active ? "danger" : "primary"} onClick={() => setOpen(true)}>{active ? "Suspend game" : "Reactivate game"}</Button>
      <GameStatusDialog game={game} open={open} onClose={() => setOpen(false)} />
    </Panel>
  );
}

function DeletePanel({ game }: { game: Game }) {
  const [open, setOpen] = useState(false);
  return (
    <Panel title="Delete game" sub="Closes the game for good. You can't undo this.">
      <ul className="muted" style={{ margin: "0 0 14px", paddingLeft: 20, display: "grid", gap: 4 }}>
        <li>Every API key is revoked at once, in sandbox and production.</li>
        <li>The game's clock and scheduled jobs stop, and it disappears from your games.</li>
        <li>Its ledger and audit history are kept. The slug <span className="mono">{game.slug}</span> becomes free to reuse.</li>
      </ul>
      <Button variant="danger" onClick={() => setOpen(true)}>Delete this game</Button>
      <DeleteGameDialog game={game} open={open} onClose={() => setOpen(false)}
        // Leave the page first so it doesn't refetch the deleted game and show a 404.
        onDeleted={() => navigate("/dev/games")} />
    </Panel>
  );
}

/** Confirms suspending an active game, or reactivating a suspended one. Shared with the games list. */
export function GameStatusDialog({ game, open, onClose }: { game: Game; open: boolean; onClose: () => void }) {
  const api = developerApi(useClient());
  const toast = useToast();
  const active = game.status === "active";
  const change = useAction(() => api.updateGame(game.id, { status: active ? "suspended" : "active" }));
  const close = () => {
    change.setError(null);
    onClose();
  };
  return (
    <Confirm
      open={open}
      title={active ? `Suspend ${game.name}?` : `Reactivate ${game.name}?`}
      body={active
        ? "Game servers using this game's keys are refused from their next request. Balances, keys and history stay intact."
        : "Its unrevoked keys work again immediately."}
      confirmLabel={active ? "Suspend game" : "Reactivate game"}
      danger={active}
      busy={change.pending}
      error={change.error}
      onConfirm={async () => {
        if (await change.run()) {
          close();
          invalidate("dev:game"); // also refreshes "dev:games"
          toast(active ? `Suspended ${game.name}` : `Reactivated ${game.name}`);
        }
      }}
      onClose={close}
    />
  );
}

/** Confirms deleting a game; the developer types its name first. Shared with the games list. */
export function DeleteGameDialog({ game, open, onClose, onDeleted }: { game: Game; open: boolean; onClose: () => void; onDeleted?: () => void }) {
  const api = developerApi(useClient());
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const del = useAction(async () => {
    await api.deleteGame(game.id);
    return true;
  });
  const close = () => {
    setTyped("");
    del.setError(null);
    onClose();
  };
  return (
    <Confirm
      open={open}
      title={`Delete ${game.name}?`}
      body="Every API key is revoked and game servers using them stop working immediately. Ledger and audit history are kept, but the game can't be restored."
      confirmLabel="Delete game"
      danger
      busy={del.pending}
      disabled={typed !== game.name}
      error={del.error}
      onConfirm={async () => {
        if (typed !== game.name) return;
        if (await del.run()) {
          close();
          onDeleted?.();
          invalidate("dev:game");
          toast(`Deleted ${game.name}`);
        }
      }}
      onClose={close}
    >
      <Field label={`Type ${game.name} to confirm`}>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} />
      </Field>
    </Confirm>
  );
}
